/**
 * `entitySnapshotWire.ts` — INBOUND parser for Unity's JsonUtility
 * `EntitySnapshotData` event shape (`update_entitysnapshot`, tc 14 —
 * `EntitySnapshotEventData.entitySnapshot`).
 *
 * Wire contract (`Models/Data/EntitySnapshotData.cs`, verified against the
 * `[SerializeField]` names): `snapshotID`, `entityID`, `partID`, `name`,
 * `displayName`, `isRoot`, `isLock` (not `isLocked`), `visibility`,
 * `forcedVisibility`, `merged`, `position`/`rotation`/`scale`/
 * `initialPos`/`intialRot` (typo verbatim) as `[{name,val}]` pair arrays
 * with **euler-degree** rotations, `explosionFactor`, `materialIDs`,
 * `persistentSpinJson` (blank = no spin), `persistentHighlightJson`
 * (blank = not highlighted).
 *
 * The parser MERGES the wire fields onto the receiver's current snapshot:
 * a real Unity payload carries every field, so the merge is effectively a
 * full replace of the modeled surface; fields the web doesn't model
 * (states, zoom, authoring poses, …) stay untouched on the web side.
 *
 * The web's own `update_entitysnapshot` publishes the same shape
 * (`entitySnapshotToUnityWire` in `ProjectModel.ts`), so one parser covers
 * both Unity and web peers.
 */

import { readAliases } from "./aliasData";
import type { EntitySnapshot, EntityTransform, Vec3 } from "./entityData";
import { normalizeExplosionFactor } from "./explosionFactor";
import { toNameFloatMap, vec3From } from "./nameFloatMap";

/** True when the payload looks like Unity's JsonUtility snapshot shape. */
export function isWireEntitySnapshot(value: Record<string, unknown>): boolean {
  return (
    typeof value.snapshotID === "string"
    || typeof value.SnapshotID === "string"
    || Array.isArray(value.position)
  );
}

export function wireSnapshotId(value: Record<string, unknown>): string | null {
  const id = value.snapshotID ?? value.SnapshotID ?? value.partID ?? value.PartID;
  return typeof id === "string" && id.length > 0 ? id : null;
}

/**
 * Merge a wire snapshot payload onto the current web snapshot. Returns the
 * merged snapshot, or `null` when the payload carries no usable data.
 */
export function mergeWireEntitySnapshot(
  wire: Record<string, unknown>,
  current: EntitySnapshot,
): EntitySnapshot {
  const transform = mergeWireTransform(wire, current.transform);
  const resetTransform = mergeWireResetTransform(wire, current.resetTransform);

  const rawSpin = wire.persistentSpinJson ?? wire.PersistentSpinJson;
  // JsonUtility serializes a null string as "" — blank means "no spin".
  const persistentSpinJson =
    typeof rawSpin === "string"
      ? (rawSpin.trim().length > 0 ? rawSpin : null)
      : current.persistentSpinJson;

  const rawHighlight = wire.persistentHighlightJson ?? wire.PersistentHighlightJson;
  const persistentHighlightJson =
    typeof rawHighlight === "string"
      ? (rawHighlight.trim().length > 0 ? rawHighlight : null)
      : current.persistentHighlightJson;

  const explosion = wire.explosionFactor ?? wire.ExplosionFactor;

  return {
    ...current,
    name: strOr(wire.name, current.name),
    displayName: strOr(wire.displayName, current.displayName),
    visibility: boolOr(wire.visibility, current.visibility),
    forcedVisibility: boolOr(wire.forcedVisibility, current.forcedVisibility),
    // Unity wire name is `isLock` (no "ed").
    isLocked: boolOr(wire.isLock ?? wire.isLocked, current.isLocked),
    hasMergedChildren: boolOr(wire.merged, current.hasMergedChildren),
    explosionFactor:
      typeof explosion === "number" && Number.isFinite(explosion)
        ? normalizeExplosionFactor(explosion)
        : current.explosionFactor,
    materialIds: stringArrayOr(wire.materialIDs, current.materialIds),
    persistentSpinJson,
    persistentHighlightJson,
    // Part name aliases — a WEB-FIRST field with no Unity member, so it is
    // absent from a Unity peer's payload and must fall back to the receiver's
    // current list rather than clearing it. Only an explicit array counts as
    // "the sender told me", which is what `Array.isArray` gates on here.
    aliases: Array.isArray(wire.aliases ?? wire.Aliases)
      ? readAliases(wire.aliases ?? wire.Aliases)
      : current.aliases,
    transform,
    resetTransform,
  };
}

function mergeWireTransform(
  wire: Record<string, unknown>,
  current: EntityTransform,
): EntityTransform {
  const position = wireVec3(wire.position, current.position);
  const rotation = wireVec3(wire.rotation, current.rotation);
  const scale = wireVec3(wire.scale, current.scale);
  if (position === current.position && rotation === current.rotation && scale === current.scale) {
    return current;
  }
  return { position, rotation, scale };
}

function mergeWireResetTransform(
  wire: Record<string, unknown>,
  current: EntityTransform,
): EntityTransform {
  // Wire names verbatim: `initialPos` + `intialRot` (Unity's typo).
  const position = wireVec3(wire.initialPos, current.position);
  const rotation = wireVec3(wire.intialRot, current.rotation);
  if (position === current.position && rotation === current.rotation) return current;
  return { position, rotation, scale: current.scale };
}

/** Parse a `[{name,val}]` pair array (or `{x,y,z}` record); fallback when absent. */
function wireVec3(value: unknown, fallback: Vec3): Vec3 {
  if (value === undefined || value === null) return fallback;
  const map = toNameFloatMap(value);
  if (typeof map.x !== "number" && typeof map.y !== "number" && typeof map.z !== "number") {
    return fallback;
  }
  return vec3From(map, fallback as [number, number, number]);
}

function strOr(value: unknown, fallback: string): string {
  return typeof value === "string" ? value : fallback;
}

function boolOr(value: unknown, fallback: boolean): boolean {
  return typeof value === "boolean" ? value : fallback;
}

function stringArrayOr(
  value: unknown,
  fallback: ReadonlyArray<string>,
): ReadonlyArray<string> {
  if (!Array.isArray(value)) return fallback;
  return value.filter((item): item is string => typeof item === "string");
}
